import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Navigate, useSearchParams} from 'react-router-dom'
import type {PractitionersPatient} from '@common/models'
import type {PartnerPatientAppointmentResponse} from '@generated/models'

import {
  PageContainer,
  PatientHeader,
  PatientInfoRow,
  PatientInfoLabel,
  PatientInfoValue,
  PatientSelectContainer,
} from './ReportsPatientPage.styled'
import {PatientAppointmentsList} from './PatientAppointmentsList'
import {SlotDetailsModal} from './SlotDetailsModal'
import {CancelAppointmentModal} from './CancelAppointmentModal'
import {RescheduleAppointmentModal} from './RescheduleAppointmentModal'

import {useGetPartnerPatientReport} from '~/api/partnerAdminReports'
import {Select} from '~/components/general/select/Select'
import {PatientsSuggestionsList} from '~/components/general/select/PatientsSuggestionsList'
import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Loading} from '~/components/general/loading/Loading'
import {Icon} from '~/components/general/icon/Icon'
import {formatName} from '~/utils'
import {useNavigationType} from '~/hooks/useNavigationType'

const ReportsPatientPageContent = () => {
  const {t} = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [query, setQuery] = useState('')
  const [selectedPatient, setSelectedPatient] =
    useState<PractitionersPatient | null>(null)
  const [openSlotId, setOpenSlotId] = useState<string | null>(null)
  const [openCancelId, setOpenCancelId] = useState<string | null>(null)
  const [openRescheduleId, setOpenRescheduleId] = useState<string | null>(null)
  const initialPatientId = searchParams.get('patientId') || ''

  const handleQueryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newQuery = event.target.value
      setQuery(newQuery)
      // Clear selection when user starts typing
      if (newQuery && selectedPatient) {
        setSelectedPatient(null)
      }
    },
    [selectedPatient],
  )

  const handlePatientClick = useCallback(
    (patient: PractitionersPatient) => {
      setQuery('')
      setSelectedPatient(patient)
      // Clear patientId from URL when user manually selects a patient
      const current = Object.fromEntries(searchParams.entries())
      const rest = Object.fromEntries(
        Object.entries(current).filter(([key]) => key !== 'patientId'),
      )
      setSearchParams(rest)
    },
    [searchParams, setSearchParams],
  )

  const {data, isLoading} = useGetPartnerPatientReport(
    selectedPatient?.patientId || '',
    {
      enabled: !!selectedPatient?.patientId,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
    },
  )

  // Initialize patient from URL if present
  useEffect(() => {
    if (initialPatientId && !selectedPatient) {
      // Create minimal PractitionersPatient object with required fields
      const minimalPatient: PractitionersPatient = {
        patientId: initialPatientId,
        facilityId: '', // Empty string as placeholder, not used for partner admin reports
        firstName: '',
        secondName: '',
        permissions: [],
        requestedPermissions: [],
        specializations: [],
      }
      setSelectedPatient(minimalPatient)
    }
  }, [initialPatientId, selectedPatient])

  // Update patient object with data from report once loaded
  useEffect(() => {
    if (
      data &&
      selectedPatient &&
      !selectedPatient.firstName &&
      !selectedPatient.secondName
    ) {
      setSelectedPatient({
        ...selectedPatient,
        firstName: data.firstName || '',
        secondName: data.secondName || '',
      })
    }
  }, [data, selectedPatient])

  // Combine date (yyyy/mm/dd) and time (HH:mm) into a Date for comparisons/sorting
  const toDateTime = useCallback((dateStr: string, timeStr: string) => {
    const [yyyy, mm, dd] = dateStr.split('/').map((v) => Number(v))
    const [hh, min] = timeStr.split(':').map((v) => Number(v))
    // Months are 0-based in JS Date
    return new Date(yyyy, (mm || 1) - 1, dd || 1, hh || 0, min || 0)
  }, [])

  const {upcoming, past} = useMemo(() => {
    const items: PartnerPatientAppointmentResponse[] = data?.appointments ?? []
    const now = new Date()
    const upcomingItems = items
      .filter((it) => toDateTime(it.date, it.time).getTime() >= now.getTime())
      .sort(
        (a, b) =>
          toDateTime(a.date, a.time).getTime() -
          toDateTime(b.date, b.time).getTime(),
      )
    const pastItems = items
      .filter((it) => toDateTime(it.date, it.time).getTime() < now.getTime())
      .sort(
        (a, b) =>
          toDateTime(b.date, b.time).getTime() -
          toDateTime(a.date, a.time).getTime(),
      )
    return {upcoming: upcomingItems, past: pastItems}
  }, [data?.appointments, toDateTime])

  const selectedOption = useMemo(() => {
    // Only show selected option when query is empty
    if (!selectedPatient || query) return undefined
    return (
      <Typography weight="medium">{formatName(selectedPatient)}</Typography>
    )
  }, [selectedPatient, query])

  const handleClearSelection = useCallback(() => {
    setSelectedPatient(null)
    setQuery('')
    // Clear patientId from URL when clearing selection
    const current = Object.fromEntries(searchParams.entries())
    const rest = Object.fromEntries(
      Object.entries(current).filter(([key]) => key !== 'patientId'),
    )
    setSearchParams(rest)
  }, [searchParams, setSearchParams])

  // Get partnerServiceId from the selected appointment
  const cancelAppointment = useMemo(() => {
    if (!openCancelId) return null
    return data?.appointments.find((a) => a.slotId === openCancelId) || null
  }, [openCancelId, data?.appointments])

  const rescheduleAppointment = useMemo(() => {
    if (!openRescheduleId) return null
    return data?.appointments.find((a) => a.slotId === openRescheduleId) || null
  }, [openRescheduleId, data?.appointments])

  return (
    <PageContainer direction="column" spacing={4} flex fullWidth>
      <PatientSelectContainer>
        <Select
          search
          placeholder={t('Search patients...')}
          onChange={handleQueryChange}
          value={query}
          selectedOption={selectedOption}>
          <PatientsSuggestionsList
            query={query}
            onPatientClick={handlePatientClick}
            filteredPatients={selectedPatient ? [selectedPatient] : []}
          />
        </Select>
        {selectedPatient && (
          <Icon
            name="Close"
            size="sm"
            circled="md"
            onClick={handleClearSelection}
          />
        )}
      </PatientSelectContainer>

      {selectedPatient && (
        <>
          {isLoading && <Loading />}

          {!isLoading && data && (
            <>
              <PatientHeader>
                <Container direction="column" spacing={1}>
                  {data.firstName && (
                    <PatientInfoRow>
                      <PatientInfoLabel weight="medium">
                        {t('First name')}
                      </PatientInfoLabel>
                      <PatientInfoValue>{data.firstName}</PatientInfoValue>
                    </PatientInfoRow>
                  )}
                  {data.secondName && (
                    <PatientInfoRow>
                      <PatientInfoLabel weight="medium">
                        {t('Last name')}
                      </PatientInfoLabel>
                      <PatientInfoValue>{data.secondName}</PatientInfoValue>
                    </PatientInfoRow>
                  )}
                  <PatientInfoRow>
                    <PatientInfoLabel weight="medium">
                      {t('Email')}
                    </PatientInfoLabel>
                    <PatientInfoValue>{data.email}</PatientInfoValue>
                  </PatientInfoRow>
                  {data.phone && (
                    <PatientInfoRow>
                      <PatientInfoLabel weight="medium">
                        {t('Phone')}
                      </PatientInfoLabel>
                      <PatientInfoValue>{data.phone}</PatientInfoValue>
                    </PatientInfoRow>
                  )}
                  {data.dateOfBirth && (
                    <PatientInfoRow>
                      <PatientInfoLabel weight="medium">
                        {t('Date of birth')}
                      </PatientInfoLabel>
                      <PatientInfoValue>{data.dateOfBirth}</PatientInfoValue>
                    </PatientInfoRow>
                  )}
                  {data.accountCreatedAt && (
                    <PatientInfoRow>
                      <PatientInfoLabel weight="medium">
                        {t('Account created at')}
                      </PatientInfoLabel>
                      <PatientInfoValue>
                        {data.accountCreatedAt}
                      </PatientInfoValue>
                    </PatientInfoRow>
                  )}
                  <PatientInfoRow>
                    <PatientInfoLabel weight="medium">
                      {t('App access')}
                    </PatientInfoLabel>
                    <PatientInfoValue>
                      {data.appAccess === 'YES' ? t('YES') : t('NO')}
                    </PatientInfoValue>
                  </PatientInfoRow>
                </Container>
              </PatientHeader>

              <PatientAppointmentsList
                title={t('Upcoming appointments')}
                items={upcoming}
                onClickHistory={setOpenSlotId}
                onClickCancel={setOpenCancelId}
                onClickReschedule={setOpenRescheduleId}
              />
              <PatientAppointmentsList
                title={t('Past appointments')}
                items={past}
                onClickHistory={setOpenSlotId}
                onClickCancel={setOpenCancelId}
                onClickReschedule={setOpenRescheduleId}
              />
            </>
          )}
        </>
      )}

      <SlotDetailsModal
        isVisible={!!openSlotId}
        onClose={() => setOpenSlotId(null)}
        slotId={openSlotId}
      />
      {cancelAppointment && (
        <CancelAppointmentModal
          isVisible={!!openCancelId}
          onClose={() => setOpenCancelId(null)}
          slotId={cancelAppointment.slotId}
          partnerServiceId={cancelAppointment.partnerServiceId}
          onSuccess={() => {
            setOpenCancelId(null)
          }}
        />
      )}
      {rescheduleAppointment && (
        <RescheduleAppointmentModal
          isVisible={!!openRescheduleId}
          onClose={() => setOpenRescheduleId(null)}
          slotId={rescheduleAppointment.slotId}
          partnerServiceId={rescheduleAppointment.partnerServiceId}
          onSuccess={() => {
            setOpenRescheduleId(null)
          }}
        />
      )}
    </PageContainer>
  )
}

export const ReportsPatientPage = () => {
  const {isPractitionerOnly} = useNavigationType()
  if (isPractitionerOnly) {
    return <Navigate replace to="/app/reports/patients" />
  }
  return <ReportsPatientPageContent />
}
